import { AbstractBlockFactory } from '../block/AbstractBlockFactory';
import {
    BlockType,
    ComponentSettings,
    ComponentSettingsExternalVisualizationMapColumnFilter,
    DatasetFieldValueType,
    IComponentProperties,
    NewBlockContextType,
    OrderDirection,
    TextAlignType,
} from '@report-content';
import { AssocArray, YNValues } from 'tools/types';
import { ConditionFormatting } from 'components/report-content/components/dataset/edit-panel/condition-formatting';

export class BlockFactoryDataset extends AbstractBlockFactory<ComponentSettingsDataset> {
    public getNewBlock({
        nextUId,
        nextSortOrder,
        datasetFields,
    }: NewBlockContextType): BlockType<ComponentSettingsDataset> {
        const fields: Array<ComponentSettingsDatasetField> = datasetFields.map((f) => {
            return {
                columnSize: 200,
                aggregation_method: '',
                column_name: f.column_name,
                dataset_column_id: Number(f.dataset_column_id),
                dataset_filter_column_id: Number(f.dataset_filter_column_id),
                dataset_filter_id: Number(f.dataset_filter_id),
                dataset_id: Number(f.dataset_id),
                display_mask_id: f.display_mask_id,
                field: f.column_name,
                id: f.reference_name,
                reference_name: f.reference_name,
                reference_name_escaped: f.reference_name_escaped,
                show_column_in_table_display_ind: 'Y',
                value_type: f.value_type,
                override_header_ind: 'N',
                label: f.label,
                break_column: 'N',
                group_column: 'N',
                compute_result_total_ind: 'Y',
                textAlign: f.value_type == 'numeric' ? 'right' : 'left',
            };
        });
        const uid = 'block_uid_' + nextUId;
        return {
            // TODO:
            component: {
                datasetRendererBlockComponentId: nextUId,
                datasetRendererBlockId: nextUId,
                datasetRendererComponentId: nextUId,
                displayOrder: nextSortOrder,
                internalName: 'dataset',
                settings: {
                    uid: uid,
                    title: '',
                    height: 600,
                    show_in_viewer: 'Y',
                    is_preview_ind: 'N',
                    is_collapsed_ind: 'N',
                    include_in_email: 'Y',
                    include_in_email_attachment: 'Y',
                    autoStretch: true,
                    internal_name: 'dataset',
                    breakColumns: [],
                    breakArea: false,
                    groupColumns: [],
                    groupingArea: false,
                    report_index_preview_type: 'dataset',
                    report_multiple_units_label: 'rows',
                    report_no_units_label: 'No rows',
                    report_single_unit_label: 'row',
                    fields: fields,
                    fixedColumns: 0,
                    formationRulesData: [],
                    headerFormatting: {
                        textBold: true,
                    },
                    limit_total_rows: 'N',
                    mainFont: AllowedFonts[0].value,
                    mainFontSize: 12,

                    show_top_n_series: 1,
                    show_totals: 'N',
                    sortFields: [],
                    totalsSettings: {
                        textBold: true,
                    },
                    totals_label: 'Overall Total',
                    groupFilter: { operator: 'AND', rules: [] },
                    zebra_striping: 'Y',
                    showHiddenFields: 'N',
                    isOnCreation: true,
                    extViz: {
                        link_to_dataset: 'N',
                        filters: [],
                        report_ext_vis_applicable_scope: 'row',
                        report_ext_vis_element_id: 0,
                        report_max_viz_number_to_display: 0,
                        report_refer_from_email_to: 'self',
                        report_ext_vis_images_in_email: 0,
                        dataset_block_uid: '',
                    },
                },
            },
            // datasetRendererBlockId: number;
            displayOrder: nextSortOrder,
            // elementId: number;
            uid: uid,
        };
    }
}

export interface FontType {
    value: string;
    label: string;
}
export const AllowedFonts: Array<FontType> = [
    {
        value: 'font-arial',
        label: 'Arial, Helvetica, sans-serif',
    },
    {
        value: 'font-georgia',
        label: 'Georgia, serif',
    },
    {
        value: 'font-tahoma',
        label: 'Tahoma, Geneva, sans-serif',
    },
    {
        value: 'font-times',
        label: 'Times New Roman, Times, serif',
    },
    {
        value: 'font-verdana',
        label: 'Verdana, Geneva, sans-serif',
    },
];
export const AllowedFontSizes: Array<number> = [10, 11, 12, 13, 14, 16, 18];

export type DataRowType = AssocArray<string | number | null>;

export interface AdditionalContentSettingsDataset extends IComponentProperties {
    page: number;
    getAmount: YNValues;
    getTotals: YNValues;
}

export interface DatasetInitialDataResponse {
    '0': { cnt: number };
    totals: { '0': DataRowType };
}

export interface FieldTextFormatting {
    bgColor?: string;
    textWrap?: boolean;
    textBold?: boolean;
    textItalic?: boolean;
    textColor?: string;
    textAlign?: TextAlignType;
    applyTo?: string | -1;
}

export interface TransformIntermediateData {
    type: 'image' | 'link';
    link: string;
    image: string;
    imageWidth: string;
    imageHeight: string;
    imageType: 'fill' | 'fit';
}

export interface ComponentSettingsDatasetField extends FieldTextFormatting {
    // aggregate_function: '';
    // aggregation: false;
    aggregation_method: string;
    columnSize: number;
    // column_instance: 'current';
    column_name: string;
    // column_sequence: 0;
    compute_result_total_ind: YNValues;
    dataset_column_id: number;
    dataset_filter_column_id: number;
    dataset_filter_id: number;
    dataset_id: number;
    display_mask_id: string | null;
    field: string;
    id: string;
    // instance: 'current';
    // is_derived_ind: 'N';
    // is_result_ind: 'Y';
    // key: 'calendar_date-current';
    // label: 'calendar_date';
    // label_new: 'calendar_date';
    // name: 'calendar_date';
    // ref_column_name: 'calendar_date';
    transform_label?: string;
    transform_intermediate_data?: TransformIntermediateData;
    reference_name: string;
    reference_name_escaped: string;
    show_column_in_table_display_ind: YNValues;
    value_type: DatasetFieldValueType;
    override_header_ind?: YNValues;
    label?: string;
    break_column: YNValues;
    group_column: YNValues;
}

export interface ComponentSettingsDatasetHeader extends FieldTextFormatting {
    value_type?: string;
}

export interface ComponentSettingsDatasetSortField {
    column_name: string;
    dir: OrderDirection;
    reference_name: string;
    reference_name_escaped: string;
    value_type: DatasetFieldValueType;
}

export interface ComponentSettingsDatasetExtViz {
    link_to_dataset: YNValues | 'D';
    filters: Array<ComponentSettingsExternalVisualizationMapColumnFilter>;
    report_ext_vis_element_id: number;
    report_ext_vis_applicable_scope: 'row' | 'once';
    // report_ext_vis_display: string;
    report_max_viz_number_to_display: number;
    report_ext_vis_images_in_email: number;
    // report_ext_vis_iframe_height: number;
    report_refer_from_email_to: string;
    dataset_block_uid: string;
}

export interface ComponentSettingsTableLike extends ComponentSettings {
    uid: string;
    autoStretch: boolean;
    // breakArea: false;
    // breakColumns: [];
    // component_name: 'Table';
    fields: Array<ComponentSettingsDatasetField>;
    mainFont: string;
    mainFontSize: number;
    zebra_striping: YNValues;
}

export interface ComponentSettingsDataset extends ComponentSettingsTableLike {
    fixedColumns: number;
    formationRulesData: Array<ConditionFormatting>;
    // formattedFields: [];
    groupColumns: [] | [number, number];
    groupingArea: boolean;
    breakColumns: [] | [number, number];
    breakArea: boolean;
    headerFormatting: ComponentSettingsDatasetHeader;
    // internal_name: 'dataset';
    // isAdditionalAggregation: false;
    // is_preview_ind: 'Y';
    limit_total_rows: YNValues;
    // no_data_label: 'No Data';
    // no_data_label_settings: { textAlign: 'left' };
    report_index_preview_type: string;
    report_multiple_units_label: string;
    report_no_units_label: string;
    report_single_unit_label: string;
    // showHiddenFields: 'N';
    // show_title: 'N';
    show_top_n_series: number;
    show_totals: YNValues;
    sortFields: Array<ComponentSettingsDatasetSortField>;
    totalsSettings: FieldTextFormatting;
    totals_label: string;
    zebra_striping: YNValues;
    showHiddenFields: YNValues;
    currentInnerWidth?: number;
    extViz: ComponentSettingsDatasetExtViz;
}
